import { useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useAuth } from './useAuth'

interface NotificationData {
  title: string
  message: string
  type: string
  data?: any
}

export const useNotificationIntegration = () => {
  const { user, profile } = useAuth()

  const sendPushNotification = async (
    userIds: string[],
    title: string,
    body: string,
    data?: any
  ) => {
    if (!user) return

    try {
      const { error } = await supabase.functions.invoke('send-push-notification', {
        body: {
          userIds,
          title,
          body,
          data: {
            ...data,
            timestamp: Date.now()
          }
        }
      })

      if (error) {
        console.error('Error sending push notification:', error)
      }
    } catch (error) {
      console.error('Error sending push notification:', error)
    }
  }

  // Listen for database changes and send push notifications
  useEffect(() => {
    if (!user || !profile) return

    console.log('Setting up real-time notification listeners for user:', user.id)

    // Listen for new messages
    const messagesChannel = supabase
      .channel('messages-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'messages',
          filter: `sender_id=neq.${user.id}` // Don't notify for own messages
        },
        async (payload) => {
          console.log('New message received:', payload)
          
          // Get conversation participants to check if user is involved
          const { data: participants } = await supabase
            .from('conversation_participants')
            .select('user_id')
            .eq('conversation_id', payload.new.conversation_id)

          const isParticipant = participants?.some(p => p.user_id === user.id)
          
          if (isParticipant && payload.new.message_type !== 'system') {
            // Get sender info
            const { data: sender } = await supabase
              .from('profiles')
              .select('full_name')
              .eq('id', payload.new.sender_id)
              .single()

            await sendPushNotification(
              [user.id],
              `Nova mensagem de ${sender?.full_name || 'Usuário'}`,
              payload.new.content.substring(0, 100) + (payload.new.content.length > 100 ? '...' : ''),
              {
                type: 'message',
                url: '/mensagens',
                conversationId: payload.new.conversation_id
              }
            )
          }
        }
      )
      .subscribe()

    // Listen for new incidents (for sindicos and admins)
    let incidentsChannel: any = null
    if (profile.role && ['sindico', 'administracao', 'superadmin'].includes(profile.role)) {
      incidentsChannel = supabase
        .channel('incidents-notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'incidents'
          },
          async (payload) => {
            if (payload.new.reported_by === user.id) return // Don't notify the creator

            console.log('New incident created:', payload)

            await sendPushNotification(
              [user.id],
              'Nova Ocorrência Registrada',
              `${payload.new.title} - ${payload.new.description.substring(0, 100)}`,
              {
                type: 'incident',
                url: '/ocorrencias',
                incidentId: payload.new.id
              }
            )
          }
        )
        .subscribe()
    }

    // Listen for new packages (for residents)
    let packagesChannel: any = null
    if (profile.role === 'morador') {
      packagesChannel = supabase
        .channel('packages-notifications')
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'packages'
          },
          async (payload) => {
            console.log('New package registered:', payload)

            // Check if this package is for the current user's unit
            const { data: userUnits } = await supabase
              .from('units')
              .select('id')
              .eq('owner_id', user.id)

            const isForUser = userUnits?.some(unit => unit.id === payload.new.unit_id)

            if (isForUser) {
              await sendPushNotification(
                [user.id],
                'Nova Encomenda Recebida',
                `Encomenda de ${payload.new.sender || 'remetente não informado'} foi registrada`,
                {
                  type: 'package',
                  url: '/encomendas',
                  packageId: payload.new.id
                }
              )
            }
          }
        )
        .subscribe()
    }

    // Listen for new announcements
    const announcementsChannel = supabase
      .channel('announcements-notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'announcements'
        },
        async (payload) => {
          if (payload.new.author_id === user.id) return // Don't notify the author

          console.log('New announcement published:', payload)

          await sendPushNotification(
            [user.id],
            payload.new.is_important ? '📢 Aviso Importante!' : '📋 Novo Aviso',
            payload.new.title,
            {
              type: 'announcement',
              url: '/avisos',
              announcementId: payload.new.id
            }
          )
        }
      )
      .subscribe()

    // Cleanup subscriptions
    return () => {
      console.log('Cleaning up notification listeners')
      supabase.removeChannel(messagesChannel)
      if (incidentsChannel) supabase.removeChannel(incidentsChannel)
      if (packagesChannel) supabase.removeChannel(packagesChannel)
      supabase.removeChannel(announcementsChannel)
    }
  }, [user, profile])

  return {
    sendPushNotification
  }
}